import { useRef } from 'react'
import {
  createLocalFirstStore,
  createSyncEngine,
  action,
  sync,
  receiptAsyncAction,
  rowsToCamel,
} from '@aurora/platform'
import { AURORA_PLATFORM_CONFIG } from './registry'

/**
 * The Aurora client store.
 *
 * Five collections mirroring the five primitives. Structure that used to live
 * in tables (plans, rubrics, skills) now lives inside artifact `data`, so the
 * agent can reshape it without a client release.
 *
 * Decorators handle pending protection, IndexedDB write-through and the durable
 * outbox; action bodies are plain mutative drafts.
 */

type Collection<T = any> = Record<string, T>

export type ViewKey = 'thread' | 'today' | 'story' | 'studio' | 'library' | 'child' | 'kid'

export interface UiState {
  view: ViewKey
  activeChildId?: string
  activeThreadId?: string
  selectedArtifactId?: string
  selectedCaptureId?: string
  selectedDate?: string
  /** The material being generated or previewed, before it is kept. */
  draftMaterialId?: string
  /** The plan block Kid Mode was entered from, so the result lands on it. */
  kidBlockRef?: string
  compare?: [string, string]
  theme: 'light' | 'dark' | 'system'
  online: boolean
  composing: string
  /**
   * True once the on-disk store has been read back. Until then a collection
   * being empty means "not loaded yet", not "nothing here" — the difference
   * between a skeleton and telling a parent their library is empty.
   */
  hydrated: boolean
}

export interface CaptureState {
  recording: boolean
  liveTranscript: string
  pendingCaptureId?: string
  /** The plan block this capture belongs to, when it started from one. */
  blockRef?: string
  /** Ids awaiting interpretation — drives the "thinking" affordance. */
  interpreting: string[]
  error?: string
}

export interface AuroraState {
  children: Collection
  threads: Collection
  messages: Collection
  artifacts: Collection
  captures: Collection
  memories: Collection
  schedules: Collection

  // the spine
  skills: Collection
  skillStates: Collection
  cycles: Collection
  weeks: Collection
  days: Collection
  blocks: Collection
  levelHistory: Collection
  reviews: Collection

  account: any | null
  clientState: Record<string, unknown>
  pending: Record<string, unknown>
  syncMeta: Record<string, number>

  ui: UiState
  capture: CaptureState

  [key: string]: unknown
}

const now = () => Date.now()
/** Reviews are one-per-period: their client id is the period itself. */
export const reviewClientId = (period: string, date: string) => `review_${period}_${date}`
/** Client stub id; the platform rekeys these onto server ids on sync. */
const stub = () => `stub_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`

/**
 * Locate a plan block by id.
 *
 * Blocks are not rows — they live inside the plan artifact's body, in whatever
 * shape the agent authored. Both writers that touch a block go through here so
 * the shape-tolerance lives in one place, matching `blocksForDate` on the read
 * side. An unrecognised shape means "not found", never a crash.
 */
function findPlanBlock(d: AuroraState, blockId: string): { plan: any; block: any } | undefined {
  for (const plan of Object.values(d.artifacts) as any[]) {
    if (plan?.kind !== 'plan' || plan.deleted) continue
    const data = plan.data
    if (!data) continue
    const days: any[] =
      (Array.isArray(data.days) && data.days) ||
      (Array.isArray(data.weeks) && data.weeks.flatMap((w: any) => w?.days ?? [])) ||
      []
    for (const day of days) {
      for (const block of day?.blocks ?? []) {
        if (block?.id === blockId) return { plan, block }
      }
    }
  }
  return undefined
}

/**
 * Locate one skill inside a rubric artifact.
 *
 * Same reasoning as `findPlanBlock`: skills live in agent-authored artifact
 * data, so the one writer that edits them keeps the shape-tolerance here.
 */
function rubricSkillDraft(
  d: AuroraState,
  artifactId: string,
  domainKey: string,
  skillKey: string,
): any | undefined {
  const domains = (d.artifacts[artifactId] as any)?.data?.domains
  if (!Array.isArray(domains)) return undefined
  const domain = domains.find((dom: any) => (dom?.key ?? dom?.name) === domainKey)
  return (domain?.skills ?? []).find((sk: any) => (sk?.key ?? sk?.name) === skillKey)
}

const engine = createSyncEngine(AURORA_PLATFORM_CONFIG)

const creator = (set: any, get: any) => ({
  children: {},
  threads: {},
  messages: {},
  artifacts: {},
  captures: {},
  memories: {},
  schedules: {},

  // the spine
  skills: {},
  skillStates: {},
  cycles: {},
  weeks: {},
  days: {},
  blocks: {},
  levelHistory: {},
  reviews: {},

  account: null,
  clientState: {},
  pending: {},
  syncMeta: {},

  ui: {
    view: 'thread',
    theme: 'system',
    online: true,
    composing: '',
    hydrated: false,
  } as UiState,

  capture: {
    recording: false,
    liveTranscript: '',
    interpreting: [],
  } as CaptureState,

  // ── view state ─────────────────────────────────────────────

  setView: sync(function (this: AuroraState, view: ViewKey) { this.ui.view = view }),
  setActiveChild: action(function (this: AuroraState, childId: string) {
    this.ui.activeChildId = childId
    this.clientState.activeChildId = childId
  }),

  /**
   * Onboarding writes the child locally first, so the app is usable before the
   * server has ever been reached. Pronouns are always passed explicitly — the
   * caller defaults them, and nothing anywhere infers them from the name.
   */
  addChild: action(function (this: AuroraState, input: {
        name: string
        birthDate: string
        pronouns: { subject: string; object: string; possessive: string }
        timezone: string
      },) {
      const id = stub()
      const ts = now()
      this.children[id] = { _id: id, ...input, client_id: id, createdAt: ts, updatedAt: ts }
      this.ui.activeChildId = id
      this.clientState.activeChildId = id
    },
  ),
  setTheme: action(function (this: AuroraState, theme: UiState['theme']) {
    this.ui.theme = theme
    this.clientState.theme = theme
  }),
  selectThread: action(function (this: AuroraState, threadId?: string) {
    this.ui.activeThreadId = threadId
    this.clientState.activeThreadId = threadId
  }),
  selectArtifact: sync(function (this: AuroraState, id?: string) { this.ui.selectedArtifactId = id }),
  selectDate: sync(function (this: AuroraState, date?: string) { this.ui.selectedDate = date }),
  setCompare: sync(function (this: AuroraState, ids?: [string, string]) { this.ui.compare = ids }),
  setComposing: sync(function (this: AuroraState, text: string) { this.ui.composing = text }),
  setOnline: sync(function (this: AuroraState, online: boolean) { this.ui.online = online }),
  setHydrated: sync(function (this: AuroraState) { this.ui.hydrated = true }),

  /**
   * Incoming server rows, applied through the engine's pending protection.
   * Rows arrive snake_case and the store speaks camelCase; the conversion
   * happens here so no selector ever needs a fallback chain.
   */
  syncTable: sync(function (this: AuroraState, field: string, incoming: any, opts?: any) {
    engine.syncTable(this, field, rowsToCamel(incoming), opts)
  }),

  // ── capture ────────────────────────────────────────────────

  /** `blockRef` ties the capture to the activity it came from, when there is one. */
  startRecording: sync(function (this: AuroraState, blockRef?: string) {
    this.capture.recording = true
    this.capture.liveTranscript = ''
    this.capture.blockRef = blockRef
    this.capture.error = undefined
  }),
  updateLiveTranscript: sync(function (this: AuroraState, text: string) {
    this.capture.liveTranscript = text
  }),
  cancelRecording: sync(function (this: AuroraState) {
    this.capture.recording = false
    this.capture.liveTranscript = ''
    this.capture.blockRef = undefined
  }),

  /**
   * Optimistic and must-deliver. The capture exists the instant this returns
   * and the outbox guarantees it lands, however long the device is offline.
   */
  logVoiceCapture: action(function (this: AuroraState, input: { childId: string; transcript: string; threadId?: string; blockRef?: string; clientId?: string }) {
      const id = input.clientId ?? stub()
      input.clientId = id
      const ts = now()
      this.captures[id] = {
        _id: id,
        childId: input.childId,
        occurredAt: ts,
        capturedAt: ts,
        source: 'voice',
        raw: { transcript: input.transcript },
        threadId: input.threadId,
        blockRef: input.blockRef,
        interpretationStatus: 'pending',
        client_id: id,
        updatedAt: ts,
      }
      this.capture.recording = false
      this.capture.liveTranscript = ''
      this.capture.blockRef = undefined
      this.capture.pendingCaptureId = id
      this.capture.interpreting = [...this.capture.interpreting, id]
    },
  ),

  logTextCapture: action(function (this: AuroraState, input: { childId: string; text: string; threadId?: string; blockRef?: string; clientId?: string }) {
      const id = input.clientId ?? stub()
      input.clientId = id
      const ts = now()
      this.captures[id] = {
        _id: id,
        childId: input.childId,
        occurredAt: ts,
        capturedAt: ts,
        source: 'text',
        raw: { text: input.text },
        threadId: input.threadId,
        blockRef: input.blockRef,
        interpretationStatus: 'pending',
        client_id: id,
        updatedAt: ts,
      }
      this.capture.interpreting = [...this.capture.interpreting, id]
    },
  ),

  logQuickCapture: action(function (this: AuroraState, input: { childId: string; rating: number; blockRef?: string }) {
      const id = stub()
      const ts = now()
      this.captures[id] = {
        _id: id,
        childId: input.childId,
        occurredAt: ts,
        capturedAt: ts,
        source: 'quick',
        raw: { rating: input.rating },
        blockRef: input.blockRef,
        client_id: id,
        updatedAt: ts,
      }
    },
  ),

  editInterpretation: action(function (this: AuroraState, captureId: string, interpretation: any) {
    const c = this.captures[captureId]
    if (!c) return
    c.interpretation = interpretation
    c.parentEdited = true
    c.updatedAt = now()
  }),

  clearInterpreting: sync(function (this: AuroraState, captureId: string) {
    this.capture.interpreting = this.capture.interpreting.filter((i) => i !== captureId)
    if (this.capture.pendingCaptureId === captureId) this.capture.pendingCaptureId = undefined
  }),

  // ── thread ─────────────────────────────────────────────────

  /**
   * Receipt-backed: an agent turn can run for a while and must survive a
   * reload. The promise resolves from the durable receipt after replay.
   */
  sendMessage: receiptAsyncAction(function (this: AuroraState, input: {
      childId: string
      threadId?: string
      content: string
      artifactIds?: string[]
      messageClientId?: string
      threadClientId?: string
    }) {
      const ts = now()
      let threadId = input.threadId
      if (!threadId) {
        threadId = stub()
        input.threadClientId = threadId
        this.threads[threadId] = {
          _id: threadId,
          childId: input.childId,
          title: input.content.slice(0, 60) || 'New thread',
          lastMessageAt: ts,
          client_id: threadId,
          updatedAt: ts,
        }
        this.ui.activeThreadId = threadId
      }

      const messageId = input.messageClientId ?? stub()
      input.messageClientId = messageId
      this.messages[messageId] = {
        _id: messageId,
        threadId,
        childId: input.childId,
        role: 'user',
        content: input.content,
        attachments: input.artifactIds?.map((id) => ({ kind: 'artifact', artifactId: id })),
        createdAt: ts,
        client_id: messageId,
        updatedAt: ts,
      }

      const t = this.threads[threadId]
      if (t) t.lastMessageAt = ts
      this.ui.composing = ''
    },
  ),

  newThread: action(function (this: AuroraState, childId: string, title = 'New thread') {
    const id = stub()
    const ts = now()
    this.threads[id] = {
      _id: id, childId, title, lastMessageAt: ts, client_id: id, updatedAt: ts,
    }
    this.ui.activeThreadId = id
    this.ui.view = 'thread'
  }),

  archiveThread: action(function (this: AuroraState, threadId: string) {
    const t = this.threads[threadId]
    if (!t) return
    t.archived = true
    t.updatedAt = now()
  }),

  // ── artifacts ──────────────────────────────────────────────

  /**
   * One writer for every artifact the client authors — a photo, a baseline
   * rubric, a keepsake book. Kind and body are the caller's business; anything
   * the agent later revises comes back through sync as a new version.
   */
  createArtifact: action(function (this: AuroraState, input: {
        childId: string
        kind: string
        title: string
        summary?: string
        data?: any
        body?: string
        html?: string
        storage?: any
        source?: 'child' | 'parent' | 'agent'
        tags?: string[]
      },) {
      const id = stub()
      const ts = now()
      this.artifacts[id] = {
        ...input,
        _id: id,
        source: input.source ?? 'parent',
        version: 1,
        isCurrent: true,
        tags: input.tags ?? [],
        analysisStatus: input.kind === 'photo' ? 'pending' : undefined,
        createdAt: ts,
        client_id: id,
        updatedAt: ts,
      }
      this.ui.selectedArtifactId = id
    },
  ),

  /** The parent can edit anything the agent made — it is their record. */
  saveArtifactEdit: action(function (this: AuroraState, artifactId: string, patch: { title?: string; body?: string; data?: any }) {
      const a = this.artifacts[artifactId]
      if (!a) return
      Object.assign(a, patch)
      a.updatedAt = now()
    },
  ),

  pinArtifact: action(function (this: AuroraState, artifactId: string, pinned: boolean) {
    const a = this.artifacts[artifactId]
    if (!a) return
    a.pinned = pinned
    a.updatedAt = now()
  }),

  // ── rubric ─────────────────────────────────────────────────

  /**
   * The parent is ground truth.
   *
   * An override pins the level and stays visible as a parent judgement, so the
   * agent can never quietly argue with it. The level the agent had inferred is
   * kept alongside, which is what makes the override reversible: clearing it
   * hands the skill back rather than freezing whatever number was last shown.
   */
  // ── spine actions: the parent's thin writable surface ──────
  // Checking off a block and pinning a level are the two writes a parent makes
  // dozens of times a week; both are optimistic and offline-safe.

  setBlockStatus: action(function (this: AuroraState, blockId: string, status: 'pending' | 'done' | 'partial' | 'skipped') {
      const block = this.blocks[blockId]
      if (!block) return
      block.status = status
      block.completedAt = status === 'done' || status === 'partial' ? now() : undefined
      block.updatedAt = now()
    },
  ),

  /** The Notes column of the paper plan: one line on a block. */
  setBlockNote: action(function (this: AuroraState, blockId: string, note: string) {
    const block = this.blocks[blockId]
    if (!block) return
    block.note = note.trim() || undefined
    block.updatedAt = now()
  }),

  /**
   * The evening check-in. Every block on the day gets a status (anything still
   * pending is marked skipped — an honest record beats a hopeful one), notes
   * land on the blocks, and the day's review row records that the day was
   * closed. Notes also become captures so evidence reaches the rubric.
   */
  closeDay: action(function (this: AuroraState, input: {
    childId: string
    date: string
    blocks: Array<{ blockId: string; status: 'done' | 'partial' | 'skipped'; note?: string }>
    notes?: string
  }) {
    const ts = now()
    for (const b of input.blocks) {
      const block = this.blocks[b.blockId]
      if (!block) continue
      block.status = b.status
      block.completedAt = b.status === 'skipped' ? undefined : ts
      block.note = b.note?.trim() || block.note
      block.updatedAt = ts
    }
    const existing = Object.values(this.reviews).find(
      (r: any) => r.childId === input.childId && r.period === 'day' && r.date === input.date && !r.deleted,
    ) as any
    // Deterministic client id: the server row carries the same one, so the
    // optimistic row rekeys onto it and a retry can never make a second row.
    const id = existing?._id ?? reviewClientId('day', input.date)
    this.reviews[id] = {
      ...(existing ?? { _id: id, childId: input.childId, period: 'day', date: input.date, client_id: id }),
      parent: { ...(existing?.parent ?? {}), notes: input.notes?.trim() || undefined, submittedAt: ts },
      status: 'submitted',
      updatedAt: ts,
    }
  }),

  /** The weekly (or cycle) check-in table: ratings, answers, win, adjust. */
  submitReview: action(function (this: AuroraState, input: {
    childId: string
    period: 'week' | 'cycle'
    date: string
    weekId?: string
    cycleId?: string
    ratings: Array<{ domainKey: string; rating: number }>
    answers?: Array<{ question: string; answer: string }>
    win?: string
    adjust?: string
    notes?: string
  }) {
    const ts = now()
    const existing = Object.values(this.reviews).find(
      (r: any) => r.childId === input.childId && r.period === input.period && r.date === input.date && !r.deleted,
    ) as any
    const id = existing?._id ?? reviewClientId(input.period, input.date)
    this.reviews[id] = {
      ...(existing ?? { _id: id, childId: input.childId, period: input.period, date: input.date, client_id: id }),
      weekId: input.weekId ?? existing?.weekId,
      cycleId: input.cycleId ?? existing?.cycleId,
      parent: {
        ratings: input.ratings,
        answers: input.answers,
        win: input.win?.trim() || undefined,
        adjust: input.adjust?.trim() || undefined,
        notes: input.notes?.trim() || undefined,
        submittedAt: ts,
      },
      status: 'submitted',
      updatedAt: ts,
    }
  }),

  /** Pin a level on a spine skill. The pin always wins and stays visible. */
  pinSkillLevel: action(function (this: AuroraState, input: { childId: string; skillKey: string; level: number; note?: string }) {
    const { skillKey, level, note } = input
    const state = Object.values(this.skillStates).find(
      (st: any) => (st.skillKey ?? st.skill_key) === skillKey,
    ) as any
    if (!state) return
    const inferred = state.parentOverride?.inferredLevel ?? state.level
    state.parentOverride = { level, note, at: now(), inferredLevel: inferred }
    // `level` itself is server-owned; the pinSkillLevel side effect moves it
    // and writes the history row. Optimistically mirror it here.
    state.level = level
    state.updatedAt = now()
  }),

  unpinSkillLevel: action(function (this: AuroraState, input: { childId: string; skillKey: string }) {
    const { skillKey } = input
    const state = Object.values(this.skillStates).find(
      (st: any) => (st.skillKey ?? st.skill_key) === skillKey,
    ) as any
    if (!state?.parentOverride) return
    const inferred = state.parentOverride.inferredLevel
    if (typeof inferred === 'number') state.level = inferred
    state.parentOverride = undefined
    state.updatedAt = now()
  }),

  setSkillNote: action(function (this: AuroraState, skillKey: string, note: string) {
    const state = Object.values(this.skillStates).find(
      (st: any) => (st.skillKey ?? st.skill_key) === skillKey,
    ) as any
    if (!state) return
    state.note = note
    state.updatedAt = now()
  }),

  overrideSkillLevel: action(function (this: AuroraState, artifactId: string,
      domainKey: string,
      skillKey: string,
      level: number,
      note?: string,) {
      const skill = rubricSkillDraft(this, artifactId, domainKey, skillKey)
      if (!skill) return
      const inferred = skill.parentOverride?.inferredLevel ?? skill.level
      skill.parentOverride = { level, note, at: now(), inferredLevel: inferred }
      skill.level = level
      this.artifacts[artifactId].updatedAt = now()
    },
  ),

  clearSkillOverride: action(function (this: AuroraState, artifactId: string, domainKey: string, skillKey: string) {
      const skill = rubricSkillDraft(this, artifactId, domainKey, skillKey)
      if (!skill?.parentOverride) return
      const inferred = skill.parentOverride.inferredLevel
      if (typeof inferred === 'number') skill.level = inferred
      skill.parentOverride = undefined
      this.artifacts[artifactId].updatedAt = now()
    },
  ),

  /** Soft delete — sync carries the tombstone; selectors already skip it. */
  deleteArtifact: action(function (this: AuroraState, artifactId: string) {
    const a = this.artifacts[artifactId]
    if (!a) return
    a.deleted = true
    a.updatedAt = now()
    if (this.ui.selectedArtifactId === artifactId) this.ui.selectedArtifactId = undefined
    if (this.ui.draftMaterialId === artifactId) this.ui.draftMaterialId = undefined
  }),

  // ── materials: generated interactives ──────────────────────

  /**
   * Ask for a playable material.
   *
   * The artifact exists the instant this returns, in a `generating` state, so
   * progress is a property of the thing itself rather than of a modal. The
   * parent can close the app mid-generation and find it finished on return —
   * the receipt survives a reload and the outbox survives being offline.
   */
  generateMaterial: receiptAsyncAction(function (this: AuroraState, input: { childId: string; prompt: string; kind?: string; blockRef?: string },) {
      const id = stub()
      const ts = now()
      this.artifacts[id] = {
        _id: id,
        childId: input.childId,
        kind: 'activity',
        title: input.prompt.trim().slice(0, 60) || 'New activity',
        source: 'agent',
        version: 1,
        isCurrent: true,
        /** Drafts stay out of the library until the parent keeps them. */
        draft: true,
        blockRef: input.blockRef,
        generation: {
          prompt: input.prompt,
          requestedKind: input.kind,
          status: 'generating',
          requestedAt: ts,
        },
        tags: [],
        createdAt: ts,
        client_id: id,
        updatedAt: ts,
      }
      this.ui.draftMaterialId = id
    },
  ),

  /** Preview looked right — it joins the library. */
  keepMaterial: action(function (this: AuroraState, artifactId: string) {
    const a = this.artifacts[artifactId]
    if (!a) return
    a.draft = false
    a.updatedAt = now()
    if (this.ui.draftMaterialId === artifactId) this.ui.draftMaterialId = undefined
  }),

  clearDraftMaterial: sync(function (this: AuroraState) { this.ui.draftMaterialId = undefined }),

  /**
   * Attach a material to a plan block. Blocks live inside the plan artifact's
   * body, so this edits the plan rather than a block row.
   */
  attachMaterialToBlock: action(function (this: AuroraState, artifactId: string, blockId: string) {
      const found = findPlanBlock(this, blockId)
      if (!found || !this.artifacts[artifactId]) return
      const ids: string[] = Array.isArray(found.block.artifactIds) ? found.block.artifactIds : []
      if (!ids.includes(artifactId)) found.block.artifactIds = [...ids, artifactId]
      found.plan.updatedAt = now()
    },
  ),

  // ── kid mode ───────────────────────────────────────────────

  enterKidMode: sync(function (this: AuroraState, artifactId: string, blockRef?: string) {
    this.ui.selectedArtifactId = artifactId
    this.ui.kidBlockRef = blockRef
    this.ui.view = 'kid'
  }),

  exitKidMode: sync(function (this: AuroraState, back: ViewKey = 'studio') {
    this.ui.kidBlockRef = undefined
    this.ui.view = back
  }),

  /**
   * She finished something. Playing IS an observation, so this must deliver:
   * the evidence mapping runs server-side (one place owns spec → skills), and
   * locally we reflect only what the parent can see — the material's history
   * and the block it came from.
   */
  logInteractiveResult: action(function (this: AuroraState, input: { childId: string; artifactId: string; result: any; blockRef?: string },) {
      const ts = now()
      const a = this.artifacts[input.artifactId]
      if (a) {
        a.lastPlayedAt = ts
        a.playCount = (a.playCount ?? 0) + 1
        a.updatedAt = ts
      }
      if (!input.blockRef) return
      const found = findPlanBlock(this, input.blockRef)
      if (!found || found.block.status === 'done') return
      found.block.status = input.result?.completed ? 'done' : 'partial'
      found.block.completedAt = ts
      found.plan.updatedAt = ts
    },
  ),

  // ── incoming sync ──────────────────────────────────────────

  setAccount: sync(function (this: AuroraState, account: any, clientState: any) {
    this.account = account
    if (clientState) this.clientState = { ...this.clientState, ...clientState }
  }),
})

const local = createLocalFirstStore<AuroraState>(AURORA_PLATFORM_CONFIG, creator as any)

/**
 * Content-stable selector hook.
 *
 * Selectors here freely build derived arrays and objects (`threads(s)`,
 * `rubricDomains(s)`), which means a fresh reference on every call — and React
 * 19's useSyncExternalStore treats an unstable snapshot as a state change,
 * looping forever. Rather than pushing memo discipline into every selector and
 * component, the hook stabilizes at the boundary: when a recomputed result is
 * content-equal to the previous one, the previous reference is returned and
 * React sees no change.
 *
 * The equality walk is cheap relative to what it prevents (render churn on
 * every store tick for every subscribed component), and view models here are
 * small. Rows keep their identity through the sync engine, so most comparisons
 * short-circuit on the first Object.is.
 */
function contentEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false
  if (Array.isArray(a)) {
    if (a.length !== (b as unknown[]).length) return false
    for (let i = 0; i < a.length; i++) {
      if (!contentEqual(a[i], (b as unknown[])[i])) return false
    }
    return true
  }
  const ak = Object.keys(a as object)
  const bk = Object.keys(b as object)
  if (ak.length !== bk.length) return false
  for (const k of ak) {
    if (!contentEqual((a as any)[k], (b as any)[k])) return false
  }
  return true
}

function useAuroraStoreImpl(): AuroraState
function useAuroraStoreImpl<T>(selector: (s: AuroraState) => T): T
function useAuroraStoreImpl<T>(selector?: (s: AuroraState) => T) {
  const ref = useRef<{ value: T } | null>(null)
  // Both branches keep hook order stable: the selector-less form still passes
  // a (trivial) selector through the same code path.
  return (local.useStore as any)((s: AuroraState) => {
    if (!selector) return s
    const next = selector(s)
    if (ref.current && contentEqual(ref.current.value, next)) return ref.current.value
    ref.current = { value: next }
    return next
  })
}

export const useAuroraStore = Object.assign(useAuroraStoreImpl, local.useStore)
export const auroraStore = local.useStore
export const useTracked = local.useTrackedStore
export const syncEngine = engine
export const platformMaps = local.maps

// Debug handle for automated verification; harmless in production.
if (typeof window !== 'undefined') (window as any).__AURORA__ = local.useStore
